import React, { useEffect, useState } from 'react';
import { useSync } from '../context/SyncContext';
import { Cloud, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';
import { formatDateHeure } from '../lib/formats';

// Âge de la dernière mise à jour: savoir qu'un poste a 2 jours de retard plutôt
// que 2 minutes est tout l'intérêt de l'indicateur. Sans mise à jour connue, on
// n'écrit rien: une icône seule.
export const formatSyncAge = (isoString, now = Date.now()) => {
  if (!isoString) return '';
  const timestamp = new Date(isoString).getTime();
  if (Number.isNaN(timestamp)) return '';

  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < 60) return "à l'instant";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  return `il y a ${days} j`;
};

// Une icône et l'heure de la dernière mise à jour: aucun mot sur le réseau, la
// file ou l'état de l'envoi. Le mécanisme est invisible.
const SyncIndicator = ({ inline = false, className } = {}) => {
  const { isSyncing, lastSyncTime, triggerSync } = useSync();

  // Re-render periodically so the relative age stays truthful without any event.
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const syncAge = formatSyncAge(lastSyncTime);
  const isStale = lastSyncTime
    ? Date.now() - new Date(lastSyncTime).getTime() > 60 * 60 * 1000
    : false;

  return (
    <div
      className={cn(
        'sync-indicator transition-colors',
        inline && 'sync-indicator-inline',
        className,
      )}
      data-testid="sync-indicator"
    >
      {isSyncing ? (
        <RefreshCw className="w-4 h-4 animate-sync-spin text-accent-info" aria-hidden="true" />
      ) : (
        <Cloud className="w-4 h-4 text-accent-success" aria-hidden="true" />
      )}

      {syncAge && (
        <span
          className={cn(
            'text-xs whitespace-nowrap',
            isStale ? 'text-accent-warning' : 'text-muted-foreground',
          )}
          title={`Dernière mise à jour : ${formatDateHeure(lastSyncTime)}`}
          data-testid="last-sync-time"
        >
          {syncAge}
        </span>
      )}

      {!isSyncing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            triggerSync();
          }}
          className="ml-2 p-1 hover:bg-muted rounded transition-colors"
          title="Mettre à jour maintenant"
          aria-label="Mettre à jour maintenant"
          data-testid="sync-now-btn"
        >
          <RefreshCw className="w-3 h-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SyncIndicator;
